import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  Box,
  Flex,
  Text,
  Radio,
  RadioGroup,
  Stack,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Center,
  useBreakpointValue,
} from "@chakra-ui/react";
import { theme } from "src/styles/theme";
import { FaTable } from "react-icons/fa6";
import { MdOutlineShowChart } from "react-icons/md";
import quotesServiceInstance from "@services/Quotes/QuotesService";
import Alert from "@components/common/Alert";
import { LoginContext } from "@context/loginContext";

type TipoVisualizacao = "escalonado" | "linear" | "comparativo";
type TipoView = "tabela" | "grafico";

interface Parcela {
  numero: number;
  data: string;
  serie: string;
  obraMes: string;
  obraAcum: string;
  parcelaObra: string;
  taxas: string;
  proSoluto: string;
  total: string;
  comprRendaParcial: string;
}

interface ParcelaComparativa {
  numero: number;
  serie: string;
  proSolutoLinear: string;
  proSolutoEscalonado: string;
  totalLinear: string;
  totalEscalonado: string;
  comprRendaParLinear: string;
  comprRendaParEscalonado: string;
  comprRendaTotLinear: string;
  comprRendaTotEscalonado: string;
}

interface SummaryData {
  cliente: string;
  proposta: string;
  financiadoBanco: string;
  subsidio: string;
  desconto: string;
  fgts: string;
}

interface ApiResponse {
  parcelas: Parcela[]; // linear
  parcelasEscalonado: Parcela[]; // escalonado
  parcelasComparativas: ParcelaComparativa[];
  summary: SummaryData;
}

interface TabelaEscalonadoLinearProps {
  parcelas: Parcela[];
  tipo: TipoVisualizacao;
}

const TabelaEscalonadoLinear: React.FC<TabelaEscalonadoLinearProps> = ({
  parcelas,
  tipo,
}) => {
  const fontSize = useBreakpointValue({ base: "11px", md: "12px", lg: "13px" });
  const px = useBreakpointValue({ base: 2, md: 3 });

  return (
    <Box overflowX="auto" overflowY="auto" maxH="340px" w="100%">
      <Table
        size="md"
        variant="simple"
        minH={"350px"}
        style={{ borderCollapse: "collapse" }}
      >
        <Thead>
          <Tr position="sticky" top={0} zIndex={1} bg="white">
            {[
              "Parcela",
              "Data",
              "Série",
              "Parcela Obra",
              "Pro Soluto",
              "Total",
              "Compr. Renda Parcial",
            ].map((header) => (
              <Th
                key={header}
                fontSize={fontSize}
                fontWeight={"medium"}
                px={px}
                py={3}
                color={theme.text}
                borderColor={theme.disable100}
                whiteSpace="nowrap"
                textTransform="none"
                w={header === "Parcela" ? "100px" : "160px"}
                maxW={header === "Parcela" ? "100px" : "160px"}
                isNumeric={!["Parcela", "Data", "Série"].includes(header)}
              >
                {header}
              </Th>
            ))}
          </Tr>
        </Thead>

        <Tbody>
          {parcelas.map((parcela) => (
            <Tr
              key={`${parcela.numero}-${parcela.data}-${parcela.serie}`}
              _hover={{ bg: "gray.50" }}
              borderColor={theme.disable}
            >
              <Td
                fontSize={fontSize}
                px={px}
                py={2}
                color="gray.700"
                w={"100px"}
                maxW={"100px"}
              >
                {parcela.numero}
              </Td>
              <Td
                fontSize={fontSize}
                px={px}
                py={2}
                color="gray.700"
                whiteSpace="nowrap"
                w={"160px"}
                maxW={"160px"}
              >
                {parcela.data}
              </Td>
              <Td
                fontSize={fontSize}
                px={px}
                py={2}
                color="gray.500"
                whiteSpace="nowrap"
                w={"160px"}
                maxW={"160px"}
              >
                {parcela.serie}
              </Td>
              {/* <Td
                fontSize={fontSize}
                px={px}
                py={2}
                isNumeric
                bg={tipo === "escalonado" ? "#ECF1F9" : "#FEF3F4"}
                color={theme.text}
                w={"160px"}
                maxW={"160px"}
              >
                {parcela.obraMes}
              </Td> */}
              {/* <Td
                fontSize={fontSize}
                px={px}
                py={2}
                isNumeric
                bg={tipo === "escalonado" ? "#ECF1F9" : "#FEF3F4"}
                color={theme.text}
                w={"160px"}
                maxW={"160px"}
              >
                {parcela.obraAcum}
              </Td> */}
              <Td
                fontSize={fontSize}
                px={px}
                py={2}
                color="gray.700"
                w={"160px"}
                maxW={"160px"}
                isNumeric
              >
                {parcela.parcelaObra}
              </Td>
              {/* <Td
                fontSize={fontSize}
                px={px}
                py={2}
                color="gray.700"
                w={"160px"}
                maxW={"160px"}
                isNumeric
              >
                {parcela.taxas}
              </Td> */}
              <Td
                fontSize={fontSize}
                px={px}
                py={2}
                isNumeric
                bg={tipo === "escalonado" ? "#ECF1F9" : "#FEF3F4"}
                color={theme.text}
                w={"160px"}
                maxW={"160px"}
              >
                {parcela.proSoluto}
              </Td>
              <Td
                fontSize={fontSize}
                px={px}
                py={2}
                color="gray.700"
                w={"160px"}
                maxW={"160px"}
                isNumeric
              >
                {parcela.total}
              </Td>
              <Td
                fontSize={fontSize}
                px={px}
                py={2}
                w={"160px"}
                maxW={"160px"}
                isNumeric
                bg="#FEF3F4"
                color={theme.text}
              >
                {parcela.comprRendaParcial}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};

interface TabelaComparativaProps {
  parcelas: ParcelaComparativa[];
  tipo: TipoVisualizacao;
}

const TabelaComparativa: React.FC<TabelaComparativaProps> = ({
  parcelas,
  tipo,
}) => {
  const fontSize = useBreakpointValue({ base: "10px", md: "11px", lg: "12px" });
  const px = useBreakpointValue({ base: 1, md: 2 });

  const colGroupStyle = {
    borderLeft: "2px solid",
    borderColor: "gray.200",
  };

  const linearStyle = {
    bg: "#FEF3F4",
    color: theme.text,
  };

  const escalonadoStyle = {
    bg: "#ECF1F9",
    color: theme.text,
  };

  return (
    <Box overflowX="auto" overflowY="auto" maxH="340px" w="100%">
      <Table
        size="lg"
        variant="simple"
        minH={"350px"}
        style={{ borderCollapse: "collapse" }}
      >
        <Thead>
          {/* Grupo de cabeçalhos */}
          <Tr position="sticky" top={0} zIndex={1} bg="white">
            <Th
              fontSize={fontSize}
              px={px}
              py={2}
              rowSpan={2}
              borderColor="gray.200"
              borderBottom="1px"
              w="40px"
              maxW="40px"
            />
            <Th
              fontSize={fontSize}
              px={px}
              py={2}
              rowSpan={2}
              borderColor="gray.200"
              borderBottom="1px"
              color="gray.600"
              w="140px"
              maxW="140px"
              textTransform="none"
            >
              Série
            </Th>
            <Th
              fontSize={fontSize}
              px={px}
              py={2}
              colSpan={2}
              textAlign="center"
              borderBottom="1px"
              color="gray.700"
              w="160px"
              textTransform="none"
              {...colGroupStyle}
            >
              Pro Soluto
            </Th>
            <Th
              fontSize={fontSize}
              px={px}
              py={2}
              colSpan={2}
              textAlign="center"
              borderBottom="1px"
              color="gray.700"
              w="160px"
              textTransform="none"
              {...colGroupStyle}
            >
              Total
            </Th>
            <Th
              fontSize={fontSize}
              px={px}
              py={2}
              colSpan={2}
              textAlign="center"
              borderBottom="1px"
              color="gray.700"
              w="180px"
              textTransform="none"
              {...colGroupStyle}
            >
              Compr. Renda Parcial
            </Th>
            <Th
              fontSize={fontSize}
              px={px}
              py={2}
              colSpan={2}
              textAlign="center"
              borderBottom="1px"
              color="gray.700"
              w="180px"
              textTransform="none"
              {...colGroupStyle}
            >
              Compr. Renda Total
            </Th>
          </Tr>
          <Tr position="sticky" top="36px" zIndex={1} bg="white">
            {[
              "Linear",
              "Escalonado",
              "Linear",
              "Escalonado",
              "Linear",
              "Escalonado",
              "Linear",
              "Escalonado",
            ].map((label, idx) => (
              <Th
                key={idx}
                fontSize={fontSize}
                px={px}
                py={1}
                color={theme.text}
                fontWeight="500"
                borderColor="gray.200"
                borderBottom="1px"
                isNumeric
                w="80px"
                textTransform="none"
                borderLeft={idx % 2 === 0 ? "2px solid" : undefined}
                borderLeftColor={idx % 2 === 0 ? "gray.200" : undefined}
                bg={idx % 2 === 0 ? "#FEF3F4" : "#ECF1F9"}
              >
                {label}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {parcelas.map((p) => (
            <Tr
              key={`${p.numero}-${p.serie}`}
              _hover={{ opacity: 0.8 }}
              borderBottom="1px"
              borderColor="gray.100"
            >
              <Td
                fontSize={fontSize}
                px={px}
                py={2}
                color="gray.700"
                w="40px"
                maxW="40px"
              >
                {p.numero}
              </Td>
              <Td
                fontSize={fontSize}
                px={px}
                py={2}
                color="gray.500"
                whiteSpace="nowrap"
                w="140px"
                maxW="140px"
              >
                {p.serie}
              </Td>
              {[
                p.proSolutoLinear,
                p.proSolutoEscalonado,
                p.totalLinear,
                p.totalEscalonado,
                p.comprRendaParLinear,
                p.comprRendaParEscalonado,
                p.comprRendaTotLinear,
                p.comprRendaTotEscalonado,
              ].map((val, idx) => {
                const isLinear = idx % 2 === 0;

                return (
                  <Td
                    key={idx}
                    fontSize={fontSize}
                    px={px}
                    py={2}
                    isNumeric
                    borderLeft={isLinear ? "2px solid" : undefined}
                    borderLeftColor={isLinear ? "gray.200" : undefined}
                    w={"180px"}
                    {...(isLinear ? linearStyle : escalonadoStyle)}
                  >
                    {val}
                  </Td>
                );
              })}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};

interface FooterProps {
  summary: SummaryData;
}

const FooterSummary: React.FC<FooterProps> = ({ summary }) => {
  const fontSize = useBreakpointValue({ base: "11px", md: "12px" });
  const wrap = useBreakpointValue<"wrap" | "nowrap">({
    base: "wrap",
    md: "nowrap",
  });

  return (
    <Box pt={3} mt={2} borderTop="1px solid" borderColor="gray.200">
      <Flex gap={{ base: 4, md: 6 }} wrap={wrap} rowGap={2}>
        <Box minW="120px">
          <Text fontSize={fontSize} color="gray.500" mb="1px">
            Cliente
          </Text>
          <Text fontSize={fontSize} fontWeight="600" color="gray.800">
            {summary.cliente}
          </Text>
        </Box>
        <Box>
          <Text fontSize={fontSize} color="gray.500" mb="1px">
            Proposta
          </Text>
          <Text fontSize={fontSize} fontWeight="600" color="gray.800">
            {summary.proposta}
          </Text>
        </Box>
        <Box>
          <Text fontSize={fontSize} color="gray.500" mb="1px">
            Financiado pelo banco
          </Text>
          <Text fontSize={fontSize} fontWeight="600" color="gray.800">
            {summary.financiadoBanco}
          </Text>
        </Box>
        <Box>
          <Text fontSize={fontSize} color="gray.500" mb="1px">
            Subsídio
          </Text>
          <Text fontSize={fontSize} fontWeight="600" color="gray.800">
            {summary.subsidio}
          </Text>
        </Box>
        <Box>
          <Text fontSize={fontSize} color="gray.500" mb="1px">
            Desconto
          </Text>
          <Text fontSize={fontSize} fontWeight="600" color="gray.800">
            {summary.desconto}
          </Text>
        </Box>
        <Box>
          <Text fontSize={fontSize} color="gray.500" mb="1px">
            FGTS
          </Text>
          <Text fontSize={fontSize} fontWeight="600" color="gray.800">
            {summary.fgts}
          </Text>
        </Box>
      </Flex>
    </Box>
  );
};

interface ComprometimentoRendaModalProps {
  isOpen: boolean;
  onClose: () => void;
  quoteId: string;
}

export const ComprometimentoRendaModal: React.FC<
  ComprometimentoRendaModalProps
> = ({ isOpen, onClose, quoteId }) => {
  const { signOut } = React.useContext(LoginContext);
  const [tipo, setTipo] = useState<TipoVisualizacao>("escalonado");
  const [view, setView] = useState<TipoView>("tabela");
  const [data, setData] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [isUnauthorized, setIsUnauthorized] = React.useState(false);

  const modalSize = useBreakpointValue({ base: "full", md: "4xl", xl: "6xl" });

  const formatCurrency = (value: number | null): string => {
    if (value === null || value === undefined) {
      return (0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    }
    return value.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  const formatPercent = (value: number | null): string => {
    if (value === null || value === undefined) return "0,00%";
    return `${(value * 100).toFixed(2)}%`;
  };

  const formatDate = (value: string | null): string => {
    if (!value) return "—";
    return value.split("T")[0].split("-").reverse().join("/");
  };

  const fetchComprometimentoRenda = async (): Promise<ApiResponse | null> => {
    try {
      const response =
        await quotesServiceInstance.getComprometimentoRenda(quoteId);

      if (response.status === 401) {
        setIsUnauthorized(true);
        return null;
      }

      if (!response?.tabelaCompRenda || response.status >= 400) {
        console.error("Erro ao buscar comprometimento de renda", response);
        return null;
      }

      const items: any[] = Array.isArray(response?.tabelaCompRenda)
        ? response?.tabelaCompRenda
        : [];

      const mapParcela = (item: any): Parcela => ({
        numero: item.numParcelasCustom ?? 0,
        data: formatDate(item.dataDaPropostaCustom),
        serie: item.serie || item.nomeSeries || "—",
        obraMes: formatPercent(item.obraMesCustom),
        obraAcum: formatPercent(item.obraAcumuladoCustom),
        parcelaObra: formatCurrency(item.jurosDeObraIteradoCustom),
        taxas: formatCurrency(item.jurosTaxaIteradoCustom),
        proSoluto: formatCurrency(item.proSolutoLinear),
        total: formatCurrency(item.totalCustomReajustado),
        comprRendaParcial: formatPercent(
          item.comprometimentoDeRendaParcialCustom,
        ),
      });

      const mapParcelaEscalonado = (item: any): Parcela => ({
        numero: item.numParcelasCustom ?? 0,
        data: formatDate(item.dataDaPropostaCustom),
        serie: item.serie || item.nomeSeries || "—",
        obraMes: formatPercent(item.obraMesCustom),
        obraAcum: formatPercent(item.obraAcumuladoCustom),
        parcelaObra: formatCurrency(item.jurosDeObraIteradoCustom),
        taxas: formatCurrency(item.jurosTaxaIteradoCustom),
        proSoluto: formatCurrency(item.valorParcelaFluxoEscalonado),
        total: formatCurrency(item.totalFluxoEscalonado),
        comprRendaParcial: formatPercent(
          item.comprometimentoDeRendaParcialFluxoEscalonado,
        ),
      });

      const mapParcelaComparativa = (item: any): ParcelaComparativa => ({
        numero: item.numParcelasCustom ?? 0,
        serie: item.serie || item.nomeSeries || "—",
        proSolutoLinear: formatCurrency(item.proSolutoLinear),
        proSolutoEscalonado: formatCurrency(item.valorParcelaFluxoEscalonado),
        totalLinear: formatCurrency(item.totalCustomReajustado),
        totalEscalonado: formatCurrency(item.totalFluxoEscalonado),
        comprRendaParLinear: formatPercent(
          item.comprometimentoDeRendaParcialCustom,
        ),
        comprRendaParEscalonado: formatPercent(
          item.comprometimentoDeRendaParcialFluxoEscalonado,
        ),
        comprRendaTotLinear: formatPercent(
          item.comprometimentoDeRendaTotalCustom,
        ),
        comprRendaTotEscalonado: formatPercent(
          item.comprometimentoDeRendaTotalFluxoEscalonado,
        ),
      });

      const footer = items[0];

      return {
        parcelas: items.map(mapParcela), // linear
        parcelasEscalonado: items.map(mapParcelaEscalonado), // escalonado
        parcelasComparativas: items.map(mapParcelaComparativa),
        summary: {
          cliente: response?.nomeCliente || "—",
          proposta: formatCurrency(footer?.valorTotal ?? null),
          financiadoBanco: formatCurrency(footer?.financiadoPeloBanco ?? null),
          subsidio: formatCurrency(footer?.subsidio ?? null),
          desconto: formatCurrency(footer?.descontoValor ?? null),
          fgts: formatCurrency(footer?.fgts ?? null),
        },
      };
    } catch (error) {
      console.error("ErrorFetchComprometimentoRenda", error);
      return null;
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    fetchComprometimentoRenda()
      .then((res) => {
        if (res) setData(res);
      })
      .finally(() => setLoading(false));
  }, [isOpen]);

  return (
    <>
      <Alert
        onOpen={isUnauthorized}
        title="Sessão expirada"
        titleButton={"Fazer login novamente"}
        description={
          "Sua sessão expirou, faça login novamente para ter acesso."
        }
        onClick={() => {
          signOut();
        }}
      />
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size={modalSize}
        scrollBehavior="inside"
        isCentered
      >
        <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(2px)" />
        <ModalContent
          borderRadius={{ base: 0, md: "lg" }}
          mx={{ base: 0, md: 4 }}
          my={{ base: 0, md: 8 }}
          overflow="hidden"
          minH={"580px"}
        >
          <ModalCloseButton top={3} right={4} color="gray.500" />

          <ModalBody px={{ base: 4, md: 6 }} bg="white">
            {/* Header */}
            <Flex py={2}>
              <Text
                w={"fit-content"}
                fontWeight={"600"}
                fontSize={{ base: "15px", md: "17px" }}
              >
                Comprometimento de renda
              </Text>
            </Flex>
            {/* Radio - tipo */}
            <RadioGroup
              value={tipo}
              onChange={(v) => setTipo(v as TipoVisualizacao)}
              mb={4}
              w={"fit-content"}
              py={2}
            >
              <Stack direction="row" spacing={5}>
                {(
                  [
                    ["escalonado", "Escalonado"],
                    ["linear", "Linear"],
                    ["comparativo", "Comparativo"],
                  ] as const
                ).map(([val, label]) => (
                  <Radio key={val} value={val} size="sm" colorScheme="blue">
                    <Text
                      fontSize="13px"
                      color={tipo === val ? theme.primary : "gray.600"}
                      fontWeight={tipo === val ? "500" : "400"}
                      w={"fit-content"}
                    >
                      {label}
                    </Text>
                  </Radio>
                ))}
              </Stack>
            </RadioGroup>

            {/* View toggle */}
            <Flex
              gap={0}
              mb={2}
              borderBottom="2px solid"
              borderColor="gray.200"
              w="fit-content"
            >
              {(
                [
                  ["tabela", "Ver como tabela"],
                  // ["grafico", "Ver como gráfico"],
                ] as const
              ).map(([val, label]) => (
                <Button
                  key={val}
                  variant="unstyled"
                  size="sm"
                  px={3}
                  pb="6px"
                  pt={0}
                  fontSize="13px"
                  fontWeight={view === val ? "600" : "400"}
                  color={view === val ? theme.primary : "gray.500"}
                  borderBottom="2px solid"
                  borderColor={view === val ? theme.primary : "transparent"}
                  mb="-2px"
                  borderRadius={0}
                  onClick={() => setView(val)}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  _hover={{ opacity: 0.8 }}
                >
                  {val === "tabela" ? (
                    <Box w={"fit-content"}>
                      <FaTable />
                    </Box>
                  ) : (
                    <Box w={"fit-content"}>
                      <MdOutlineShowChart />
                    </Box>
                  )}
                  {label}
                </Button>
              ))}
            </Flex>

            {/* Content */}
            {loading ? (
              <Center py={12} h="340px">
                <Spinner color={theme.primary} size="md" />
              </Center>
            ) : data ? (
              <>
                {view === "tabela" ? (
                  tipo === "comparativo" ? (
                    <TabelaComparativa
                      parcelas={data.parcelasComparativas}
                      tipo={tipo}
                    />
                  ) : (
                    <TabelaEscalonadoLinear
                      parcelas={
                        tipo === "escalonado"
                          ? data.parcelasEscalonado
                          : data.parcelas
                      }
                      tipo={tipo}
                    />
                  )
                ) : (
                  <Center
                    py={20}
                    h="340px"
                    color={theme.text50}
                    fontSize="14px"
                  >
                    Visualização em gráfico não implementada.
                  </Center>
                )}

                {data.summary && <FooterSummary summary={data.summary} />}
              </>
            ) : (
              <Center py={20} h="340px" color={theme.text50} fontSize="14px">
                Nenhum dado foi encontrado.
              </Center>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ComprometimentoRendaModal;
